import { Spinner, Tabs, TabsContent, TabsList, TabsTrigger } from '@1trading/react';
import { useQueries } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/page-header';
import { fundsAssetQueryOptions } from '@/hooks/queries/treasury/use-asset-flows';
import { createAggregatedFlow, fmtDate } from './flow-helpers';
import type { AssetFlow, ViewId } from './flow-types';
import { DEFAULT_VIEWS, GROUPS, VIEWS } from './flows-constants';
import { AssetFlowsFilters } from './flows-filters';
import { AssetFlowTab } from './flows-tab';

/* ---------- Dashboard ---------- */
const AssetFlowsDashboardPage = () => {
  const narrow = useMedia('(max-width: 720px)');
  const [activeTab, setActiveTab] = useState<ViewId>(DEFAULT_VIEWS[0]);
  const [selectedViewIds, setSelectedViewIds] = useState<ViewId[]>(DEFAULT_VIEWS);
  const [pendingViewIds, setPendingViewIds] = useState<ViewId[]>(DEFAULT_VIEWS);
  const [days, setDays] = useState<number>(30);
  const [pendingDays, setPendingDays] = useState<number>(30);

  const { results, loading } = useQueries({
    queries: selectedViewIds.map((id) => fundsAssetQueryOptions(id, days)),
    combine: (qs) => ({ results: qs.map((q) => q.data), loading: qs.some((q) => q.isPending) }),
  });

  const flows: AssetFlow[] = [];
  if (!loading && results.length > 0) {
    const loaded = results as AssetFlow[];
    const aggregated = createAggregatedFlow(loaded);
    if (aggregated) {
      flows.push(aggregated);
    }
    const groupOrder = GROUPS.flatMap(([, ids]) => ids);
    const sorted = loaded.sort((a, b) => groupOrder.indexOf(a.id) - groupOrder.indexOf(b.id));
    flows.push(...sorted.map((flow) => ({ ...flow, label: VIEWS[flow.id]?.label ?? '' })));
  }

  const applyChanges = (): void => {
    setSelectedViewIds(pendingViewIds);
    setDays(pendingDays);
    setActiveTab(pendingViewIds.includes('revolut') ? 'revolut' : pendingViewIds.length > 1 ? 'aggregate' : pendingViewIds[0]);
  };

  const handleTabChange = (value: string) => {
    const tab = value as ViewId;
    setActiveTab(tab);
  };

  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));

  return (
    <div className="flex w-full flex-col gap-4 mb-4">
      <PageHeader
        title="External asset flows"
        description={`${fmtDate(start)} - ${fmtDate(end, { month: 'short', day: 'numeric', year: 'numeric' })}  · last ${days} days`}
        actions={<div className="flex flex-col gap-2 text-sm mb-2"></div>}
      />
      <div className="flex flex-col gap-6">
        <AssetFlowsFilters
          pendingViewIds={pendingViewIds}
          setPendingViewIds={setPendingViewIds}
          pendingDays={pendingDays}
          setPendingDays={setPendingDays}
          selectedViewIds={selectedViewIds}
          days={days}
          onApply={applyChanges}
        />
        <div className="flex flex-col gap-4">
          {loading && <Spinner className="size-4" />}
          {!loading && selectedViewIds.length === 0 && <div> Please select at least one source to view flows. </div>}
          {!loading && selectedViewIds.length > 0 && (
            <Tabs onValueChange={handleTabChange} value={activeTab}>
              <TabsList>
                {flows.map((flow) => (
                  <TabsTrigger key={flow.id} value={flow.id}>
                    {flow.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {flows.map((f) => (
                <TabsContent className="mt-4" value={f.id} key={f.id}>
                  <AssetFlowTab key={f.id} flow={f} narrow={narrow} />
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </div>
    </div>
  );
};

function useMedia(query: string): boolean {
  const get = (): boolean => typeof window !== 'undefined' && window.matchMedia(query).matches;
  const [m, setM] = useState<boolean>(get);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const h = (e: MediaQueryListEvent): void => setM(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, [query]);
  return m;
}

AssetFlowsDashboardPage.displayName = 'FlowsDashboardPage';

export default AssetFlowsDashboardPage;
